import { useCallback, useEffect, useRef, useState } from 'react';

export const API_URL = process.env.REACT_APP_API_URL || 'https://api.alooma.mg';

// Server limits (GET /api/uploads/config); used until the real values load.
const DEFAULT_MAX_FILES = 10;
const DEFAULT_MAX_BYTES = 500 * 1024 * 1024;

// Some browsers leave File.type empty for Office/iWork docs; S3 needs a type.
const EXTENSION_TYPES = {
  pdf: 'application/pdf',
  txt: 'text/plain',
  csv: 'text/csv',
  rtf: 'application/rtf',
  zip: 'application/zip',
  doc: 'application/msword',
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  xls: 'application/vnd.ms-excel',
  xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  ppt: 'application/vnd.ms-powerpoint',
  pptx: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  odt: 'application/vnd.oasis.opendocument.text',
  ods: 'application/vnd.oasis.opendocument.spreadsheet',
  odp: 'application/vnd.oasis.opendocument.presentation',
  pages: 'application/vnd.apple.pages',
  numbers: 'application/vnd.apple.numbers',
  key: 'application/vnd.apple.keynote',
  heic: 'image/heic',
  mov: 'video/quicktime',
};

// `accept` attribute for file pickers (mirrors the server's allowed types).
export const FILE_ACCEPT = [
  'image/*', 'video/*', 'audio/*',
  ...Object.keys(EXTENSION_TYPES).map((ext) => `.${ext}`),
].join(',');

export const contentTypeFor = (file) => {
  if (file.type) return file.type;
  const ext = (file.name.split('.').pop() || '').toLowerCase();
  return EXTENSION_TYPES[ext] || 'application/octet-stream';
};

export const formatBytes = (bytes) => {
  if (!bytes && bytes !== 0) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  return `${(bytes / 1024 / 1024 / 1024).toFixed(1)} GB`;
};

export const isImage = (att) => /^image\//.test(att?.contentType || '');
export const isVideo = (att) => /^video\//.test(att?.contentType || '');

// Read the error message from a failed API response.
async function readError(res, fallback) {
  const data = await res.json().catch(() => ({}));
  return data.message || fallback;
}

// Uploads one file: asks the API for a presigned S3 POST, then sends the bytes
// straight to S3 (fields first, file last) with progress. Resolves to the
// `{ key, name }` reference used by messages and submissions.
export function uploadFile(file, { onProgress, onXhr } = {}) {
  return (async () => {
    const res = await fetch(`${API_URL}/api/uploads`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('token')}`,
      },
      body: JSON.stringify({ fileName: file.name, contentType: contentTypeFor(file), size: file.size }),
    });
    if (!res.ok) throw new Error(await readError(res, 'Could not start the upload'));
    const upload = await res.json();

    const form = new FormData();
    Object.entries(upload.fields || {}).forEach(([k, v]) => form.append(k, v));
    form.append('file', file);

    await new Promise((resolve, reject) => {
      // No Authorization header here: S3 authorizes via the signed policy.
      const xhr = new XMLHttpRequest();
      if (onXhr) onXhr(xhr);
      xhr.open('POST', upload.url);
      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable && onProgress) onProgress(e.loaded / e.total);
      };
      xhr.onload = () => (xhr.status >= 200 && xhr.status < 300
        ? resolve()
        : reject(new Error(`Upload failed (${xhr.status})`)));
      xhr.onerror = () => reject(new Error('Upload failed. Check your connection.'));
      xhr.onabort = () => reject(new Error('Upload cancelled'));
      xhr.send(form);
    });

    return { key: upload.key, name: upload.name || file.name };
  })();
}

let nextId = 1;

// Pending attachments for a composer (chat or submission form). Files start
// uploading as soon as they are picked; each item tracks
// { id, file, name, size, progress, status: 'uploading'|'done'|'error', error, ref }.
export function usePendingUploads() {
  const [items, setItems] = useState([]);
  const [limits, setLimits] = useState({ enabled: true, maxFiles: DEFAULT_MAX_FILES, maxFileBytes: DEFAULT_MAX_BYTES });
  const [notice, setNotice] = useState('');
  const xhrs = useRef({});

  useEffect(() => {
    let cancelled = false;
    fetch(`${API_URL}/api/uploads/config`, {
      headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` },
    })
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => { if (data && !cancelled) setLimits(data); })
      .catch(() => {});
    const active = xhrs.current;
    return () => {
      cancelled = true;
      Object.values(active).forEach((xhr) => xhr.abort());
    };
  }, []);

  const patch = (id, changes) =>
    setItems((prev) => prev.map((it) => (it.id === id ? { ...it, ...changes } : it)));

  const start = useCallback((item) => {
    uploadFile(item.file, {
      onXhr: (xhr) => { xhrs.current[item.id] = xhr; },
      onProgress: (p) => patch(item.id, { progress: p }),
    })
      .then((ref) => patch(item.id, { status: 'done', progress: 1, ref }))
      .catch((err) => patch(item.id, { status: 'error', error: err.message }))
      .finally(() => { delete xhrs.current[item.id]; });
  }, []);

  const addFiles = useCallback((fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    setNotice('');
    if (!limits.enabled) {
      setNotice('File uploads are not available right now.');
      return;
    }
    const room = limits.maxFiles - items.length;
    if (files.length > room) {
      setNotice(`You can attach up to ${limits.maxFiles} files at a time.`);
    }
    const accepted = [];
    files.slice(0, Math.max(room, 0)).forEach((file) => {
      if (file.size > limits.maxFileBytes) {
        setNotice(`${file.name} is too large (max ${formatBytes(limits.maxFileBytes)}).`);
        return;
      }
      accepted.push({
        id: nextId++,
        file,
        name: file.name,
        size: file.size,
        contentType: contentTypeFor(file),
        progress: 0,
        status: 'uploading',
      });
    });
    if (!accepted.length) return;
    setItems((prev) => [...prev, ...accepted]);
    accepted.forEach(start);
  }, [items.length, limits, start]);

  const remove = useCallback((id) => {
    xhrs.current[id]?.abort();
    setItems((prev) => prev.filter((it) => it.id !== id));
  }, []);

  const retry = useCallback((id) => {
    const item = items.find((it) => it.id === id);
    if (!item) return;
    patch(id, { status: 'uploading', progress: 0, error: '' });
    start(item);
  }, [items, start]);

  const reset = useCallback(() => {
    Object.values(xhrs.current).forEach((xhr) => xhr.abort());
    setItems([]);
    setNotice('');
  }, []);

  // Put finished items back (e.g. after a failed send).
  const restore = useCallback((snapshot) => setItems(snapshot), []);

  return {
    items,
    notice,
    addFiles,
    remove,
    retry,
    reset,
    restore,
    isUploading: items.some((it) => it.status === 'uploading'),
    hasErrors: items.some((it) => it.status === 'error'),
    refs: items.filter((it) => it.status === 'done').map((it) => it.ref),
  };
}
